import { useEffect, useId, useState } from 'react';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { Alert } from '@shared/ui/Alert';
import { Panel } from '@shared/ui/Panel';

import { CaseSection } from '../_shared/components';
import { caseStateLabel } from '../_shared/copy';
import type { CaseId, CaseStateRow } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { useTransitionCase } from './mutations';

/**
 * The state control — the element this surface exists for.
 *
 * The subscriber pack states it outright: no row lets a subscriber's reply move
 * a case, and the badge is left to the operator's surface. The report entry
 * deliberately carries no state control either, so this is the one place in the
 * console a case's state is written.
 *
 * **A `<select>`, not a row of four buttons.** One value is in force at a time,
 * and four buttons would read as four independent actions rather than a choice
 * from a set. It opens on the value the badge already shows — a control
 * displaying «مفتوحة» above a case marked «بانتظار المستخدم» would lie before it
 * was touched.
 *
 * **The options are the configured vocabulary, in its configured order.** The
 * control offers the setting's list; it does not define one. When the vocabulary
 * read has not landed the control is not drawn at all — an empty `<select>`, or
 * one seeded from a compiled list, would both be worse than saying so.
 *
 * **No confirmation dialog.** A confirmation guards a destructive act, and a
 * state move is reversible with this same control; spending a modal here would
 * cheapen the one that guards a real deletion.
 */
export function CaseStateControl({
  caseId,
  caseState,
  caseStates,
  onApplied,
}: {
  caseId: CaseId;
  /** The state in force — what the control opens on. */
  caseState: string;
  /** The configured vocabulary, or `null` when the read failed or has not landed. */
  caseStates: CaseStateRow[] | null;
  onApplied: (message: string) => void;
}) {
  const fieldId = useId();
  const hintId = useId();

  const transition = useTransitionCase(caseId);
  const [selected, setSelected] = useState(caseState);
  const [formError, setFormError] = useState<string | null>(null);

  // The control follows the record: a state moved from elsewhere, or a refetch
  // after a reply, must not leave the select holding a value nothing agrees
  // with.
  useEffect(() => {
    setSelected(caseState);
  }, [caseState]);

  function apply(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    // Transitioning to the state already in force is a no-op success on the
    // wire. It is not sent at all: the toast would announce a change nobody
    // made.
    if (selected === caseState) return;

    transition.mutate(selected, {
      onSuccess: (result) => onApplied(copy.state.applied(caseStateLabel(result.caseState))),
      onError: (error) => {
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        setFormError(resolveErrorCopy(code, status, errors).message);
      },
    });
  }

  return (
    <CaseSection id="case-state" heading={copy.state.heading}>
      <Panel>
        <div className="p-4 md:p-5">
          {caseStates === null ? (
            <Alert kind="warning">{copy.state.unavailable}</Alert>
          ) : (
            <form onSubmit={apply}>
              {/*
                The select and its action sit side by side from the tablet tier:
                at the base tier a control stacked over a button leaves a tall,
                mostly-empty card. `items-end` rather than `items-center`,
                because the field is a label stacked over a control — centring
                would hang the button between the two.

                **`max-content` on the action column, and the space left over
                stays space.** The UI/UX pack fixes this row at
                `minmax(0,18rem) max-content` and says why in as many words: the
                button takes the width of its own label, and the remainder of a
                card that runs past a thousand pixels is left empty rather than
                filled — a single button stretched across half a card, or a
                muted hint promoted into a second column beside the control,
                both give the card's width to something that is not the act.
              */}
              <div className="grid gap-3 md:[align-items:end] md:[grid-template-columns:minmax(0,18rem)_max-content]">
                <div className="grid gap-2">
                  <label htmlFor={fieldId} className="text-sm font-medium text-ink">
                    {copy.state.label}
                  </label>
                  <select
                    id={fieldId}
                    value={selected}
                    onChange={(event) => setSelected(event.currentTarget.value)}
                    aria-describedby={hintId}
                    className="hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
                  >
                    {/*
                      The state in force may be one the platform no longer
                      configures. It is offered anyway, first, so the control
                      opens on the value the badge shows rather than silently
                      re-pointing at another state.
                    */}
                    {caseStates.some((row) => row.value === caseState) ? null : (
                      <option value={caseState}>{caseStateLabel(caseState)}</option>
                    )}

                    {[...caseStates]
                      .sort((left, right) => left.ordinal - right.ordinal)
                      .map((row) => (
                        <option key={row.value} value={row.value}>
                          {caseStateLabel(row.value)}
                        </option>
                      ))}
                  </select>
                </div>

                {/*
                  THE VIEW'S PRIMARY. Moving the case on is what closes it, and
                  it is the same role `ReportDetail.Decision` already draws red
                  on the sibling queue's detail — the two surfaces read the same
                  way rather than one of them having a primary and the other
                  none. The reply control above stays secondary, so §9.1's cap
                  of one red button per view still holds.
                */}
                <button
                  type="submit"
                  disabled={transition.isPending || selected === caseState}
                  className="hit-target rounded-sm border border-accent bg-accent px-4 text-sm font-semibold text-accent-ink hover:bg-accent-hover active:bg-accent-press disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {copy.state.apply}
                </button>
              </div>

              {/*
                The hint is a sibling of the row, not a child of the field:
                inside it, the field's bottom edge becomes the bottom of two
                lines of small text and nothing beside it can ever line up.
                `aria-describedby` resolves by id anywhere in the document, so
                the association is unchanged.
              */}
              <p id={hintId} className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
                {copy.state.hint}
              </p>

              {formError ? (
                <p role="alert" className="mb-0 mt-2 max-w-measure text-sm text-danger">
                  {formError}
                </p>
              ) : null}
            </form>
          )}
        </div>
      </Panel>
    </CaseSection>
  );
}
